/**
 * Unified wet/dry derivation for center pivots.
 *
 * A pivot's wet state can come from two places:
 * - The controller's CP sensor event (`centerPivotWet`), when a controller
 *   is linked. This is the authoritative source.
 * - The monitor's pressure sensor state, used as a proxy for monitor-only
 *   pivots: a pressurized line (PHI/POV) means water is flowing.
 */
import type { SensorState } from "./sensor-events"

/** Pressure states that mean the line is pressurized (water flowing). */
export const PRESSURIZED_PRESSURE_STATES = new Set<string>(["PHI", "POV"])

export type PivotWetSource = "controller" | "pressure"

export interface PivotWetStatus {
  /** true = wet, false = dry, null = unknown (no wet/dry data available) */
  isWet: boolean | null
  source: PivotWetSource | null
}

/**
 * Derive a single wet/dry state from the controller CP event and/or the
 * monitor's pressure sensor state.
 *
 * The controller is authoritative whenever it has reported a CP wet field —
 * even `cpWet === 0` alongside a pressurized monitor stays "dry" — so
 * full-install behavior is unchanged by the pressure proxy.
 *
 * PHI/POV → wet, PLO/NONE → dry, matching how the pressure badge and farm
 * timeline classify these states. There is intentionally no `readingKpa`
 * numeric fallback: the state enum is produced device-side from configured
 * thresholds, and a client-side cutoff could disagree with it.
 */
export function derivePivotWetStatus({
  cpWet,
  pressureState,
}: {
  /** Controller CP event `centerPivotWet` field (1 = wet, 0 = dry). */
  cpWet: number | null | undefined
  /** Monitor pressure/pressureSwitch `stateCurrent`. */
  pressureState: SensorState<"pressure" | "pressureSwitch"> | null | undefined
}): PivotWetStatus {
  if (cpWet != null) {
    return { isWet: cpWet === 1, source: "controller" }
  }
  if (pressureState == null) {
    return { isWet: null, source: null }
  }
  return {
    isWet: PRESSURIZED_PRESSURE_STATES.has(pressureState),
    source: "pressure",
  }
}
